import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Box, useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { SxProps } from '@mui/system';
import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  selectCurrPointId,
  setCurrPointId,
  setCurrRowNum,
  setCurrRowsideId,
  setCurrViewDirection,
} from '../select/selectSlice';
import AutoplayButton from './AutoPlayButton';
import {
  selectAutoPlay,
  selectAutoPlayDirection,
  selectAutoPlayIncrement,
  selectAutoPlaySpeed,
  selectCurrNeighborPoints,
  setAutoPlayDirection,
} from './imageSlice';
import {
  EndOfList,
  detectEndOfList,
  findNextOrPreviousPoint,
} from './utils/imageUtils';
// import PointCarousel from './PointCarousel';

const ImageNav: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currPointId = useAppSelector(selectCurrPointId);
  const currNeighborPoints = useAppSelector(selectCurrNeighborPoints);
  const autoPlay = useAppSelector(selectAutoPlay);
  const autoPlayDirection = useAppSelector(selectAutoPlayDirection);
  const autoPlaySpeed = useAppSelector(selectAutoPlaySpeed);
  const autoPlayIncrement = useAppSelector(selectAutoPlayIncrement);
  const [endOfPoints, setEndOfPoints] = useState<EndOfList>({
    forward: false,
    back: false,
    forwardAutoPlay: false,
    backAutoPlay: false,
  });

  const commonIconStyles: SxProps = {
    color: theme.colors.common.softWhite,
    m: '0.2rem',
  };

  const commonIconButtonStyles: SxProps = {
    p: 0,
    '&.Mui-disabled': {
      opacity: 0.3,
      color: theme.colors.darkTheme.grey.muted,
    },
  };

  const incrementPointAndUpdateSelectedIds = useCallback(
    (direction: 'forward' | 'back') => {
      // find the next point in the selected direction
      const point = findNextOrPreviousPoint(
        currPointId,
        currNeighborPoints,
        direction,
        autoPlay,
        autoPlayIncrement
      );

      // if there is a point, update the selected ids (rowNum, viewDirection, rowsideId, pointId, etc...)
      if (point) {
        dispatch(setCurrRowNum(point.rowside_num));
        dispatch(setCurrViewDirection(point.view_direction));
        dispatch(setCurrRowsideId(point.rowside_id));
        dispatch(setCurrPointId(point.point_id));
      }

      // if we are at the end of the points in the current direction, reverse the auto play direction
      if (autoPlay) {
        if (direction === 'forward' && endOfPoints.forwardAutoPlay) {
          dispatch(setAutoPlayDirection('back'));
        } else if (direction === 'back' && endOfPoints.backAutoPlay) {
          dispatch(setAutoPlayDirection('forward'));
        }
      }
    },
    [
      currPointId,
      currNeighborPoints,
      dispatch,
      autoPlay,
      endOfPoints,
      autoPlayIncrement,
    ]
  );

  const handleBackClick = () => {
    incrementPointAndUpdateSelectedIds('back');
  };

  const handleForwardClick = () => {
    incrementPointAndUpdateSelectedIds('forward');
  };

  // if autoplay is on, increment the point at the specified speed
  useEffect(() => {
    if (autoPlay) {
      const interval = setInterval(() => {
        incrementPointAndUpdateSelectedIds(autoPlayDirection);
      }, autoPlaySpeed);
      return () => clearInterval(interval);
    }
  }, [
    autoPlay,
    autoPlayDirection,
    autoPlaySpeed,
    incrementPointAndUpdateSelectedIds,
  ]);

  // track if we are at the end of the points in the forward or back direction
  useEffect(() => {
    const endOfPoints = detectEndOfList(
      currPointId,
      currNeighborPoints,
      autoPlayIncrement
    );
    setEndOfPoints(endOfPoints);
  }, [currPointId, currNeighborPoints, autoPlayIncrement]);

  const controlButtons = (
    <Box
      sx={{
        height: '100%',
        ml: '0.5rem',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
    >
      <AutoplayButton
        commonIconButtonStyles={commonIconButtonStyles}
        commonIconStyles={{ ...commonIconStyles }}
      />
      <IconButton
        aria-label='Back Point'
        size='small'
        onClick={handleBackClick}
        sx={{ ...commonIconButtonStyles }}
        disabled={endOfPoints.back}
      >
        <ArrowLeftIcon sx={{ ...commonIconStyles, fontSize: '2rem' }} />
      </IconButton>
      <IconButton
        aria-label='Next Point'
        size='small'
        onClick={handleForwardClick}
        sx={{ ...commonIconButtonStyles }}
        disabled={endOfPoints.forward}
      >
        <ArrowRightIcon sx={{ ...commonIconStyles, fontSize: '2rem' }} />
      </IconButton>
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        {controlButtons}
        {/* <PointCarousel /> */}
      </Box>
    </Box>
  );
};

export default ImageNav;
